import React, { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/database';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import RichTextEditor from '../components/editor/RichTextEditor';

export default function Snippets() {
  const snippets = useLiveQuery(() => db.snippets.toArray()) || [];
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ title: '', category: 'Standard', content: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const categories = ['Standard', 'Exclusions', 'Technical Specs', 'Legal Clauses', 'Pricing', 'SLAs', 'Support', 'Revisions'];

  const openModal = (snippet = null) => {
    if (snippet) {
      setEditingId(snippet.id);
      setFormData({ title: snippet.title, category: snippet.category, content: snippet.content });
    } else {
      setEditingId(null);
      setFormData({ title: '', category: 'Standard', content: '' });
    }
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) return alert("Title is required");
    const payload = { ...formData, updatedAt: Date.now() };
    if (editingId) {
      await db.snippets.update(editingId, payload);
    } else {
      await db.snippets.add({ ...payload, id: crypto.randomUUID(), createdAt: Date.now() });
    }
    setIsModalOpen(false);
  };

  const handleDelete = async (id, e) => {
    if (e) e.stopPropagation();
    if (window.confirm("Delete this snippet?")) {
      await db.snippets.delete(id);
    }
  };

  const filteredSnippets = snippets.filter(s => 
    s.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
    s.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedSnippets = filteredSnippets.reduce((acc, curr) => {
    if (!acc[curr.category]) acc[curr.category] = [];
    acc[curr.category].push(curr);
    return acc;
  }, {});

  return (
    <div className="p-8 max-w-7xl mx-auto w-full">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight uppercase">Snippet Library</h1>
          <p className="text-gray-500 mt-1 font-medium">Manage reusable content blocks for your proposals.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <SafeIcon icon={FiIcons.FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search library..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none bg-white text-sm min-w-[280px] shadow-sm" 
            />
          </div>
          <button onClick={() => openModal()} className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl font-bold transition-all uppercase tracking-wider text-xs shadow-lg shadow-indigo-100">
            <SafeIcon icon={FiIcons.FiPlus} /> New Snippet
          </button>
        </div>
      </div>

      <div className="space-y-12">
        {Object.keys(groupedSnippets).length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 border-dashed p-20 text-center text-gray-400 font-bold italic">
            No snippets found matching your search.
          </div>
        ) : (
          Object.entries(groupedSnippets).map(([category, items]) => (
            <div key={category} className="space-y-6">
              <div className="flex items-center gap-4">
                <h3 className="font-black text-gray-900 uppercase tracking-[0.2em] text-xs whitespace-nowrap">{category}</h3>
                <div className="h-px bg-gray-200 flex-1"></div>
                <span className="text-[10px] font-black text-gray-300 uppercase tracking-widest">{items.length} Items</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {items.map(snippet => (
                  <div 
                    key={snippet.id} 
                    onClick={() => openModal(snippet)}
                    className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-all group flex flex-col h-full relative overflow-hidden cursor-pointer"
                  >
                    <div className="absolute top-0 left-0 w-1 h-full bg-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="text-sm font-black text-gray-900 group-hover:text-indigo-600 transition-colors leading-tight pr-4">{snippet.title}</h4>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        <button 
                          onClick={(e) => { e.stopPropagation(); openModal(snippet); }} 
                          className="p-2 text-gray-400 hover:text-indigo-600 bg-gray-50 rounded-lg hover:bg-indigo-50 transition-colors"
                        >
                          <SafeIcon icon={FiIcons.FiEdit2} className="text-sm" />
                        </button>
                        <button 
                          onClick={(e) => handleDelete(snippet.id, e)} 
                          className="p-2 text-gray-400 hover:text-red-600 bg-gray-50 rounded-lg hover:bg-red-50 transition-colors"
                        >
                          <SafeIcon icon={FiIcons.FiTrash2} className="text-sm" />
                        </button>
                      </div>
                    </div>
                    <div className="flex-1 overflow-hidden relative">
                      <div className="prose prose-sm max-w-none text-gray-500 text-[11px] line-clamp-4 leading-relaxed sow-content" dangerouslySetInnerHTML={{ __html: snippet.content }}></div>
                      <div className="absolute bottom-0 left-0 w-full h-8 bg-gradient-to-t from-white to-transparent"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal Overlay */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col max-h-[90vh] overflow-hidden">
            <div className="px-8 py-5 border-b border-gray-100 flex justify-between items-center shrink-0 bg-gray-50/50">
              <h2 className="text-lg font-black text-gray-900 uppercase tracking-widest">{editingId ? 'Edit Snippet' : 'New Snippet'}</h2>
              <button onClick={() => setIsModalOpen(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all">
                <SafeIcon icon={FiIcons.FiX} className="text-xl" />
              </button>
            </div>
            <div className="p-8 overflow-y-auto flex-1 space-y-8">
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Snippet Title</label>
                  <input 
                    type="text" 
                    value={formData.title} 
                    onChange={e => setFormData({ ...formData, title: e.target.value })} 
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none font-bold text-gray-700 bg-gray-50/50" 
                    placeholder="e.g. Standard Web Hosting Clause" 
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Category</label>
                  <select 
                    value={formData.category} 
                    onChange={e => setFormData({ ...formData, category: e.target.value })} 
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none bg-gray-50/50 font-bold text-gray-700 cursor-pointer" 
                  >
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Content Editor</label>
                <RichTextEditor value={formData.content} onChange={val => setFormData({ ...formData, content: val })} />
              </div>
            </div>
            <div className="px-8 py-5 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 shrink-0">
              <button onClick={() => setIsModalOpen(false)} className="px-6 py-2.5 text-[11px] font-black text-gray-500 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 uppercase tracking-widest">
                Cancel
              </button>
              <button onClick={handleSave} className="px-8 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-black text-[11px] uppercase tracking-widest shadow-lg shadow-indigo-100">
                Save Snippet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}